import React, { useEffect } from 'react';
import { theme } from 'lib/style';
import { useField } from 'formik';
import { Gap } from '../styles/layout';
import styled, { css, CSSProperties } from 'styled-components/macro';
import { IoMdCloseCircle } from 'react-icons/io';
import { GoPlus } from 'react-icons/go';
import { FormikLabel } from './FormikLabel';
import { FormikErrorMessage } from './FormikErrorMessage';

interface IFormikVideoUploadProps {
  name: string;
  label?: string;
  isRequired?: boolean;
  disabled?: boolean;
  text?: string;
  info?: string | React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  extendStyles?: {
    labelStyles?: CSSProperties;
    buttonWrapper?: CSSProperties;
    asteriskStyles?: CSSProperties;
    videoStyles?: CSSProperties;
  };
  progress?: number;
}

const Video = styled.video`
  height: 145px;
  width: 100%;
  object-fit: contain;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ProgressBar = styled.div<{ progress: number }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({ progress }) => progress}%;
  background-color: ${theme.palette.covideoBlue100};
  opacity: 0.2;
  transition: width 0.2s ease-in-out;
  border-radius: 6px;
`;

type UploadBtnWrapperProps = {
  disabled?: boolean;
};
export const UploadBtnWrapper = styled.div<UploadBtnWrapperProps>`
  position: relative;
  overflow: hidden;
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
  width: 100%;
  border-radius: 6px;
  border-width: 1px;
  padding: 16px 0px;
  gap: 4px;
  background: #f7f9fa;
  color: ${theme.palette.covideoBlue100};
  font-weight: 600;
  font-size: 15px;
  line-height: 24px;
  letter-spacing: 0px;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

export const UploadInput = styled.input`
  opacity: 0;
  position: absolute;
  top: -5px;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  cursor: pointer;
  font-size: 0;
`;

export const FormikVideoUpload = ({
  isRequired = false,
  disabled = false,
  onChange,
  extendStyles,
  info,
  label,
  text = 'Upload video',
  progress,
  ...props
}: IFormikVideoUploadProps) => {
  const [field, meta, helpers] = useField(props);
  const url = field.value?.url || '';

  useEffect(() => {
    helpers.setTouched(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!!onChange) {
      onChange(e);
      return;
    }
    const file = e.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          helpers.setValue({ file: file, url: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    if (!url) return;
    helpers.setTouched(true);
    const current_file = document.getElementById(
      props.name
    ) as HTMLInputElement;
    if (!current_file) return;
    current_file.value = '';
    helpers.setValue({ url: '', file: null });
  };

  return (
    <Gap width='100%' gap='8px'>
      {label && (
        <FormikLabel
          label={label}
          isRequired={isRequired}
          disabled={!!disabled}
          info={info}
          labelStyles={extendStyles?.labelStyles}
          asteriskStyles={extendStyles?.asteriskStyles}
        />
      )}
      {url ? (
        <ImageContainer>
          <Video src={url} controls style={{ ...extendStyles?.videoStyles }} />
          <IoMdCloseCircle
            onClick={handleRemoveVideo}
            size={24}
            color={theme.palette.covideoGray60}
            style={{
              position: 'absolute',
              top: '-14px',
              right: '-14px',
              border: `3px solid ${theme.palette.white}`,
              borderRadius: '50%',
              backgroundColor: theme.palette.white,
              cursor: 'pointer',
              ...(!!disabled ? { pointerEvents: 'none', opacity: 0.5 } : {}),
            }}
          />
        </ImageContainer>
      ) : (
        <></>
      )}
      <UploadBtnWrapper
        style={{ ...extendStyles?.buttonWrapper }}
        disabled={disabled}
      >
        {!!progress && progress > 0 && <ProgressBar progress={progress} />}
        <UploadInput
          id={props.name}
          name={props.name}
          type='file'
          onChange={e => handleChange(e)}
          accept={'video/*'}
        />
        {!!progress && progress > 0 ? (
          <span>
            {progress < 100 ? `Uploading... ${progress}%` : 'Processing...'}
          </span>
        ) : url ? (
          <span>Replace video</span>
        ) : (
          <>
            <GoPlus size={20} />
            {text}
          </>
        )}
      </UploadBtnWrapper>
      <FormikErrorMessage meta={meta} />
    </Gap>
  );
};
